import type { Api } from "../api/client.js";
import type { PublicKeyCredentialCreationOptionsJSON } from "../api/client.js";
import type { AssertionOptions, Receipt } from "../api/types.js";
import type { ScamAnswer } from "./scam.js";
import { createPasskey, isCancellation, isKnownHere, isUnsupported, signNow } from "./signing.js";

/**
 * Passkey — create and sign in one flow (UX spec v3 §C.11, Addition 7; research 11
 * §1.9 and snippet D).
 *
 * The bank knows: its credential list (`GET /v1/webauthn/credentials`) says whether this
 * customer has a passkey; a passkey on the phone that the bank does not know cannot sign
 * anything. KNOWN → Sign calls `get()` at once. NOT KNOWN → the same Sign calls
 * `create()` at once; when the bank has saved it, `get()` rises by itself for the payload.
 * One tap in the app, two system sheets, no screen in between.
 *
 * Facts this rests on (research 11 Q8): since iOS 17.4 WebKit has no user-gesture rule
 * for WebAuthn (a rate limiter instead), so `get()` may follow `create()` without a new
 * tap; WebKit's `get()` rejects while the document has no focus and `create()` does not
 * check it, so the create sheet still closing can race — hence `whenFocused`, and on
 * `NotAllowedError` the card's own button (one more tap), never an automatic retry.
 * Apple passkeys return `none` attestation, so `create()` cannot also sign the payload.
 */
export type PasskeyApi = Pick<Api, "registrationVerify" | "assertionOptions" | "confirm">;

export type FirstSign =
  | { readonly kind: "signed"; readonly receipt: Receipt }
  /** Saved, but the phone refused the second sheet: the card's own button, one more tap (with these options). */
  | { readonly kind: "sign-button"; readonly options: AssertionOptions }
  | { readonly kind: "create-cancelled" }
  | { readonly kind: "timed-out" }
  | { readonly kind: "unsupported" };

export interface FirstSignDeps {
  readonly api: PasskeyApi;
  readonly credentials: CredentialsContainer;
  /** Resolves when the document has focus, or after at most a second (whenFocused). */
  readonly focus: () => Promise<void>;
  /** The bank has saved the new passkey: "Passkey saved. Now confirm the payment." */
  readonly saved: () => void;
  readonly now: () => number;
}

/** Called INSIDE the Sign tap handler: `create()` is its first statement. */
export function createThenSign(
  d: FirstSignDeps,
  reg: PublicKeyCredentialCreationOptionsJSON,
  bundleId: string,
  answers: readonly ScamAnswer[],
): Promise<FirstSign> {
  const started = d.now();
  // FIRST statement: nothing awaited before create() (the gesture rule of iOS < 17.4).
  return createPasskey(d.api, reg, d.credentials).then(
    async () => {
      d.saved();
      return signWithIt(d, bundleId, answers);
    },
    async (error: unknown): Promise<FirstSign> => {
      // The phone already holds a passkey the bank knows: nothing to create — sign with it.
      if (isKnownHere(error)) return signWithIt(d, bundleId, answers);
      if (isUnsupported(error)) return { kind: "unsupported" };
      if (isCancellation(error)) {
        const limit = reg.timeout ?? 60_000;
        return d.now() - started >= limit - 1000 ? { kind: "timed-out" } : { kind: "create-cancelled" };
      }
      throw error;
    },
  );
}

async function signWithIt(d: FirstSignDeps, bundleId: string, answers: readonly ScamAnswer[]): Promise<FirstSign> {
  // The bank knows the key now, so it hands out the payload's options (the challenge is the payload's hash).
  const options = await d.api.assertionOptions(bundleId);
  await d.focus();
  try {
    return { kind: "signed", receipt: await signNow(d.api, bundleId, options, answers, d.credentials) };
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotAllowedError") return { kind: "sign-button", options };
    throw error;
  }
}

interface FocusDoc {
  hasFocus(): boolean;
}
interface FocusWin {
  addEventListener(type: "focus", fn: () => void): void;
  removeEventListener(type: "focus", fn: () => void): void;
}

/** WebKit's `get()` rejects "The document is not focused": wait for focus, at most `ms`. */
export function whenFocused(doc: FocusDoc, win: FocusWin, ms: number): Promise<void> {
  if (doc.hasFocus()) return Promise.resolve();
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer);
      win.removeEventListener("focus", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    win.addEventListener("focus", done);
  });
}

/**
 * This device's own note that it holds a passkey the bank knows — set when a passkey
 * is created here or signs here. The bank knows a passkey and the phone offered none:
 * without this note, a refused `get()` opens the recovery sheet ("Your passkey isn't on
 * this phone") instead of the plain "Nothing was signed" line. A per-device convenience
 * in localStorage (never a credential id, never state that must persist); a storage that
 * throws (private mode) reads as "not known here".
 */
interface KeyStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
const key = (customerId: string): string => `dcta.passkey-here.${customerId}`;

export function passkeyHere(customerId: string, storage: KeyStore): boolean {
  try {
    return storage.getItem(key(customerId)) === "1";
  } catch {
    return false;
  }
}

export function markPasskeyHere(customerId: string, storage: KeyStore): void {
  try {
    storage.setItem(key(customerId), "1");
  } catch {
    /* private mode: the note is a convenience, never state that must persist */
  }
}
